// src/pages/portal/Upgrades.js
import React, { useState } from "react";
import { motion } from "framer-motion";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";

const UPGRADES = [
  { id: "payment-gateway", icon: "💳", title: "Add Payment Gateway", desc: "Integrate Maya QR, GCash, and credit card payments into your existing website.", price: "Starting at ₱25,000", badge: "Popular" },
  { id: "enrollment-system", icon: "🎓", title: "Upgrade to Enrollment System", desc: "Turn your school website into a full enrollment, tuition payment, and student management system.", price: "Starting at ₱60,000", badge: null },
  { id: "maintenance-plan", icon: "🔧", title: "Add Maintenance Plan", desc: "Monthly content updates, bug fixes, hosting monitoring, and priority support.", price: "₱3,000 – ₱8,000/mo", badge: "Recommended" },
  { id: "admin-panel", icon: "🛠️", title: "Add Admin / CMS Panel", desc: "Manage your website content, events, and pages without developer help.", price: "Starting at ₱15,000", badge: null },
  { id: "seo-boost", icon: "📈", title: "SEO & Analytics Setup", desc: "Google Search Console, GA4, structured data, and keyword optimization.", price: "Starting at ₱8,000", badge: null },
  { id: "va-addon", icon: "🤝", title: "Add Virtual Assistant", desc: "Dedicated VA support for customer service, data entry, and admin tasks.", price: "₱8,000 – ₱20,000/mo", badge: null },
];

export default function Upgrades() {
  const { currentUser } = useAuth();
  const [requested, setRequested] = useState({});
  const [loading, setLoading] = useState({});

  const handleRequest = async (upgrade) => {
    setLoading(p => ({ ...p, [upgrade.id]: true }));
    await addDoc(collection(db, "upgrade_requests"), {
      uid: currentUser.uid,
      email: currentUser.email,
      upgradeId: upgrade.id,
      upgradeName: upgrade.title,
      createdAt: serverTimestamp(),
      status: "new",
    });
    setRequested(p => ({ ...p, [upgrade.id]: true }));
    setLoading(p => ({ ...p, [upgrade.id]: false }));
  };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>Upgrades & Add-ons</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Expand your existing services with powerful add-ons. Click "Request" and we'll get in touch.</p>
      </motion.div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
        {UPGRADES.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "22px 24px", display: "flex", flexDirection: "column", height: "100%", transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--border-med)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <span style={{ fontSize: 28 }}>{u.icon}</span>
                {u.badge && <span className="badge badge-accent">{u.badge}</span>}
              </div>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: 8 }}>{u.title}</h3>
              <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.6, flex: 1, marginBottom: 14 }}>{u.desc}</p>
              <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--accent)", marginBottom: 16 }}>{u.price}</div>
              {requested[u.id] ? (
                <div style={{ padding: "10px 16px", background: "rgba(0,201,167,0.08)", border: "1px solid rgba(0,201,167,0.2)", borderRadius: 8, fontSize: "0.875rem", color: "var(--accent)", textAlign: "center" }}>
                  ✓ Request sent! We'll contact you soon.
                </div>
              ) : (
                <button
                  className="btn btn-secondary w-full"
                  style={{ justifyContent: "center" }}
                  onClick={() => handleRequest(u)}
                  disabled={loading[u.id]}
                >
                  {loading[u.id] ? "Sending..." : "Request This Upgrade"}
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
